import ApolloClient from 'apollo-client'
import { NormalizedCacheObject } from 'apollo-cache-inmemory'
import { GraphQLError } from 'graphql'

import { formatAuthorizationHeader } from 'graphql/utils'
import { getUserAuthorization } from 'graphql/multisession'
import { refreshToken, checkIfTokenIsExpired } from './refreshToken'
import { ErrorHandler } from 'modules/tracing'

type FetchType = typeof window.fetch

const UNAUTHORIZED_MESSAGES = [
  // GraphQL error message.
  'The access token expired',
  // Lambda error message
  'Invalid token',
]

const EXPIRE_TOKEN_REASONS = ['TOKEN_EXPIRED', 'ACCESS_TOKEN_EXPIRED']

const addCredentialsToOptions = (
  client: ApolloClient<NormalizedCacheObject>,
  options?: RequestInit,
  credentials = getUserAuthorization(client.cache)
) => {
  const authorization = formatAuthorizationHeader(credentials)

  const fetchOptions = authorization
    ? { ...options, headers: { ...options?.headers, authorization } }
    : options

  return fetchOptions
}

const oauthFetcher = (
  getClient: () => ApolloClient<NormalizedCacheObject>
): FetchType => async (uri, options) => {
  let credentials = getUserAuthorization(getClient().cache)

  if (credentials) {
    const isTokenExpired = checkIfTokenIsExpired(
      credentials.expirationDateTime ?? '0'
    )

    if (isTokenExpired) {
      await refreshToken(getClient())
      credentials = getUserAuthorization(getClient().cache)
    }
  }

  const response = await fetch(
    uri,
    addCredentialsToOptions(getClient(), options, credentials)
  )

  // If it doesn't have credentials then it makes no sense continuing
  if (!credentials) {
    return response
  }

  // In order to return the initial response we need to clone it before we use it
  const cloneResponse = response.clone()

  const jsonResponse = await response.json().catch(() => ({}))
  const errorsInResponse: GraphQLError[] = jsonResponse?.errors ?? []

  /**
   * When we receive a network error (e.g. 500 Internal Server Error), the body
   * does not follow SketchQL's schema. In those cases, we want to log the error
   * and return as soon as possible.
   *
   * See https://github.com/sketch-hq/Cloud/issues/6687
   */
  if (response.status === 500 || !Array.isArray(errorsInResponse)) {
    ErrorHandler.shouldNeverHappen(
      `Field "errors" is not an array: ${JSON.stringify(errorsInResponse)}`
    )
    return cloneResponse
  }

  const hasError = errorsInResponse
    .map(error => error.extensions?.['reason'])
    .find(reason => EXPIRE_TOKEN_REASONS.includes(reason))
  const hasLegacyError = UNAUTHORIZED_MESSAGES.includes(jsonResponse.message)

  if (hasError || hasLegacyError) {
    const actualCredentials = getUserAuthorization(getClient().cache)

    // We are just validating if the credentials have been updated in the meanwhile
    // from the moment the request was made until 401, because if they were we are going
    // to refresh an already refreshed token and BE will return 401 forcing a user sign-out
    // not a expected behaviour
    if (credentials.authToken !== actualCredentials?.authToken) {
      return fetch(uri, addCredentialsToOptions(getClient(), options))
    }

    const wasSuccessful = await refreshToken(getClient())

    if (!wasSuccessful) {
      // If there's an issue refreshing the token we should return the original error payload
      // because it can be more specific than the refresh token one
      return cloneResponse
    }

    return fetch(uri, addCredentialsToOptions(getClient(), options))
  }

  return cloneResponse
}

export { oauthFetcher }
